import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import Langs, { Localize } from "../../../app/lang/langs";
import {
    VLearnEmptyState, VLearnProgressBar, VLearnTypeIcon,
    formatVLearnDuration, vlearnSurface, vlearnType
} from "../vlearnUi";
import MobileLessonStatsList from "../../../containers/vlearn/mobile/MobileLessonStatsList";

/**
 * The completion rate below which a lesson is flagged.
 *
 * Half the enrolled learners is a deliberately unambitious bar: it is not a target, it is the
 * line under which a lesson is worth actually opening and looking at. A course where most
 * lessons sit above it and one sits at 12% has told the teacher exactly where to go.
 */
const ATTENTION_THRESHOLD = 50

/**
 * Per-lesson statistics — the "who saw what, and who opened what" answer.
 *
 * Rows come from `VLearnProgressController.getLessonStats`, which returns one object per
 * lesson carrying `unique_viewers`, `total_views`, `completions`, `completion_rate`,
 * `average_seconds`, `downloads` and the `enrolled` denominator those rates are computed
 * against. Nothing is recomputed here.
 *
 * ── Why the weakest lesson comes first ───────────────────────────────────────────────
 *
 * The curriculum order is the order the teacher already knows; sorting by it tells them
 * nothing they did not have. The question this table exists to answer is "which lesson is not
 * landing", so it is sorted by completion rate ascending and rows below
 * `ATTENTION_THRESHOLD` carry a marker. The finding is then the first thing on screen rather
 * than something to be found by reading eleven rows of numbers. The section and lesson
 * position are still shown on every row, so a reader who wants the curriculum order can see
 * where a flagged lesson actually sits.
 *
 * A plain MUI `Table` rather than a DataGrid: a course has tens of lessons, not thousands,
 * and real `<th scope="col">` header semantics matter more here than virtualisation.
 *
 * @param {object} props
 * @param {number} props.idCourse Only used to give the table a stable id. The rows are
 *   fetched by `CourseDashboard`, which owns the mount-time dispatches for all three tabs.
 */
export default function LessonStatsTable({ idCourse }) {

    const lessons = useSelector((state) => state.vlearn.dashboardLessons)
    const lang = useSelector((state) => state.settings.lang)

    /*
     * On a phone the 880px table shows the lesson title and nothing else — every figure, and
     * the completion rate that justifies the "needs attention" flag, is off to the right.
     * See MobileLessonStatsList.
     */
    const isMobileRedux = useSelector((state) => state.settings.isMobile)
    const theme = useTheme()
    const isNarrowQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isPhone = isMobileRedux === 2 || isNarrowQuery

    const rows = useMemo(() => {
        // Copied before sorting: the array in the slice is Redux state, and Array#sort
        // mutates in place.
        return [...(lessons || [])].sort((a, b) => {
            // Mandatory lessons first, whatever their rate. An optional lesson nobody opened
            // has a completion rate of 0 and used to sort above every mandatory lesson as the
            // "weakest" — presenting a lesson nobody was ever required to open as the most
            // urgent finding on the page. Low completion is only a finding where completion
            // was expected.
            const mandatoryA = Number(a.is_mandatory) ? 0 : 1
            const mandatoryB = Number(b.is_mandatory) ? 0 : 1
            if (mandatoryA !== mandatoryB) return mandatoryA - mandatoryB

            const rateA = Number(a.completion_rate) || 0
            const rateB = Number(b.completion_rate) || 0
            if (rateA !== rateB) return rateA - rateB
            // Ties broken by unique viewers ascending, so of two lessons nobody finished the
            // one nobody even opened is the more urgent and sorts higher.
            return (Number(a.unique_viewers) || 0) - (Number(b.unique_viewers) || 0)
        })
    }, [lessons])

    // Every row carries the same `enrolled` figure — the count the server divided by — so the
    // first row is as good a source for the caption as any.
    const enrolled = rows.length ? (Number(rows[0].enrolled) || 0) : 0

    if (!rows.length) {
        return (
            <Box sx={{ ...vlearnSurface.card }}>
                <VLearnEmptyState
                    icon={<MenuBookOutlinedIcon />}
                    title={<Langs str="VLEARN_NO_LESSONS_TITLE" />}
                    description={<Langs str="VLEARN_NO_LESSONS_BODY" />}
                />
            </Box>
        )
    }

    const headerCellSx = {
        fontSize: 12, fontWeight: 700, color: '#5a6070',
        backgroundColor: '#fafbfd', borderColor: '#f1f2f6', whiteSpace: 'nowrap'
    }
    const numericCellSx = {
        fontSize: '0.82rem', borderColor: '#f1f2f6',
        fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary, #111318)'
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>

            <Typography sx={{ ...vlearnType.meta }}>
                <Langs
                    str={enrolled === 1
                        ? "VLEARN_LESSONS_WEAKEST_FIRST_ONE"
                        : "VLEARN_LESSONS_WEAKEST_FIRST_MANY"}
                    params={{ total: enrolled }}
                />
            </Typography>

            {/*
              * The scroll lives on this container. Eight numeric columns do not fit a narrow
              * viewport, and a table allowed to widen the document makes the entire
              * application scroll sideways.
              */}
            {isPhone ? (
                <MobileLessonStatsList
                    rows={rows}
                    attentionThreshold={ATTENTION_THRESHOLD}
                    lang={lang}
                />
            ) : (
            <TableContainer sx={{ ...vlearnSurface.card, overflowX: 'auto' }}>
                <Table
                    id={`vlearn-lesson-stats-${idCourse}`}
                    size="small"
                    sx={{ minWidth: 880 }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell scope="col" sx={{ ...headerCellSx, minWidth: 260 }}>
                                <Langs str="VLEARN_COL_LESSON" />
                            </TableCell>
                            <TableCell scope="col" align="right" sx={headerCellSx}>
                                <Langs str="VLEARN_COL_UNIQUE_VIEWERS" />
                            </TableCell>
                            <TableCell scope="col" align="right" sx={headerCellSx}>
                                <Langs str="VLEARN_COL_TOTAL_VIEWS" />
                            </TableCell>
                            <TableCell scope="col" align="right" sx={headerCellSx}>
                                <Langs str="VLEARN_COL_COMPLETIONS" />
                            </TableCell>
                            <TableCell scope="col" sx={{ ...headerCellSx, minWidth: 180 }}>
                                <Langs str="VLEARN_COL_COMPLETION_RATE" />
                            </TableCell>
                            <TableCell scope="col" align="right" sx={headerCellSx}>
                                <Langs str="VLEARN_COL_AVERAGE_TIME" />
                            </TableCell>
                            <TableCell scope="col" align="right" sx={headerCellSx}>
                                <Langs str="VLEARN_COL_DOWNLOADS" />
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((lesson) => {
                            const rate = Number(lesson.completion_rate) || 0
                            // Optional lessons are never flagged: "below 50%" is a finding
                            // about a lesson the course required, and an optional one carries
                            // no such expectation to fall short of.
                            const needsAttention = rate < ATTENTION_THRESHOLD
                                && !!Number(lesson.is_mandatory)
                            const averageTime = formatVLearnDuration(lesson.average_seconds)
                            const typeLabel = Localize(
                                `VLEARN_TYPE_${String(lesson.type || 'text').toUpperCase()}`, lang)

                            return (
                                <TableRow
                                    key={lesson.id}
                                    sx={{
                                        // A tinted row AND a worded chip below. The tint alone
                                        // would be a colour-only encoding of the one thing this
                                        // table is trying to say.
                                        backgroundColor: needsAttention
                                            ? 'rgba(251,105,98,0.05)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,144,21,0.05)' }
                                    }}
                                >
                                    <TableCell sx={{ borderColor: '#f1f2f6' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                            <Tooltip title={typeLabel} arrow>
                                                <Box sx={{ display: 'flex', pt: '2px' }}>
                                                    <VLearnTypeIcon type={lesson.type} />
                                                </Box>
                                            </Tooltip>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ ...vlearnType.cardTitle }}>
                                                    {lesson.title}
                                                    {Number(lesson.is_mandatory) ? (
                                                        <Chip
                                                            size="small"
                                                            label={<Langs str="VLEARN_LESSON_MANDATORY" />}
                                                            sx={{
                                                                ml: 0.75, height: 17, fontSize: 10,
                                                                backgroundColor: 'rgba(0,40,128,0.08)',
                                                                color: 'var(--vital-blue, #002880)'
                                                            }}
                                                        />
                                                    ) : (
                                                        // Unlabelled before, which left a 0%
                                                        // optional row looking like a failure
                                                        // rather than a lesson nobody had to take.
                                                        <Chip
                                                            size="small"
                                                            label={<Langs str="VLEARN_OPTIONAL" />}
                                                            sx={{
                                                                ml: 0.75, height: 17, fontSize: 10,
                                                                backgroundColor: 'rgba(0,0,0,0.05)',
                                                                color: 'rgba(0,0,0,0.55)'
                                                            }}
                                                        />
                                                    )}
                                                </Typography>
                                                <Typography sx={{ ...vlearnType.meta }}>
                                                    {lesson.section_title || typeLabel}
                                                </Typography>
                                                {needsAttention ? (
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.4
                                                    }}>
                                                        <WarningAmberRoundedIcon
                                                            sx={{ fontSize: 13, color: '#B3271F' }}
                                                            aria-hidden="true"
                                                        />
                                                        <Typography sx={{
                                                            fontSize: '0.7rem', fontWeight: 700, color: '#B3271F'
                                                        }}>
                                                            <Langs str="VLEARN_LESSON_NEEDS_ATTENTION"
                                                                params={{ threshold: ATTENTION_THRESHOLD }} />
                                                        </Typography>
                                                    </Box>
                                                ) : null}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    <TableCell align="right" sx={numericCellSx}>
                                        {Number(lesson.unique_viewers) || 0}
                                    </TableCell>
                                    <TableCell align="right" sx={numericCellSx}>
                                        {Number(lesson.total_views) || 0}
                                    </TableCell>
                                    <TableCell align="right" sx={numericCellSx}>
                                        {Number(lesson.completions) || 0}
                                    </TableCell>

                                    <TableCell sx={{ borderColor: '#f1f2f6' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Box sx={{ flex: 1, minWidth: 70 }}>
                                                <VLearnProgressBar
                                                    percent={rate}
                                                    status={needsAttention ? 'overdue' : undefined}
                                                />
                                            </Box>
                                            {/*
                                              * The denominator travels with the rate. "38%" on
                                              * its own does not say whether it is 38% of six
                                              * people or of six hundred, and the two lead to
                                              * different decisions.
                                              */}
                                            <Tooltip
                                                title={`${Number(lesson.completions) || 0} / ${Number(lesson.enrolled) || 0}`}
                                                arrow
                                            >
                                                <Typography sx={{
                                                    fontSize: '0.82rem', fontWeight: 700, minWidth: 46,
                                                    textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                                    color: needsAttention
                                                        ? '#B3271F' : 'var(--text-primary, #111318)'
                                                }}>
                                                    {rate}%
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>

                                    <TableCell align="right" sx={numericCellSx}>
                                        {averageTime || '—'}
                                    </TableCell>
                                    <TableCell align="right" sx={numericCellSx}>
                                        {Number(lesson.downloads) || 0}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            )}

            <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                <Langs
                    str={rows.length === 1 ? "VLEARN_LESSONS_COUNT_ONE" : "VLEARN_LESSONS_COUNT_MANY"}
                    params={{ count: rows.length }}
                />
            </Typography>
        </Box>
    )
}
